import React from 'react';
import './index.less';

/**
 * Markdown渲染组件
 * 支持基础Markdown语法：标题、加粗、斜体、代码块、行内代码、链接、列表、表格
 */
const MarkdownRender = ({ content }) => {
  if (!content) return null;

  // 解析Markdown内容
  const parseMarkdown = (text) => {
    const lines = text.split('\n');
    const elements = [];
    let inCodeBlock = false;
    let codeContent = [];
    let inList = false;
    let listItems = [];
    let inTable = false;
    let tableRows = [];

    lines.forEach((line, index) => {
      // 代码块处理
      if (line.trim().startsWith('```')) {
        if (inCodeBlock) {
          // 结束代码块
          elements.push(
            <pre key={`code-${index}`} className="md-code-block">
              <code>{codeContent.join('\n')}</code>
            </pre>,
          );
          codeContent = [];
          inCodeBlock = false;
        } else {
          // 开始代码块
          inCodeBlock = true;
        }
        return;
      }

      if (inCodeBlock) {
        codeContent.push(line);
        return;
      }

      // 表格处理：识别以 | 开头和结尾的行
      const isTableLine = line.trim().match(/^\|.+\|$/);
      if (isTableLine) {
        if (!inTable) {
          inTable = true;
          tableRows = [];
        }
        tableRows.push(line);
        return;
      } else if (inTable) {
        // 表格结束，渲染表格
        elements.push(renderTable(tableRows, index));
        tableRows = [];
        inTable = false;
      }

      // 列表处理
      const isListItem = line.trim().match(/^[*-]\s/);
      if (isListItem) {
        if (!inList) {
          inList = true;
          listItems = [];
        }
        listItems.push(
          <li key={`list-${index}`}>
            {parseInline(line.replace(/^[*-]\s/, ''))}
          </li>,
        );
        return;
      } else if (inList) {
        // 列表结束
        elements.push(<ul key={`list-group-${index}`}>{listItems}</ul>);
        listItems = [];
        inList = false;
      }

      // 空行处理
      if (line.trim() === '') {
        return;
      }

      // 分隔线处理（--- 或 ***）
      if (line.trim().match(/^(---|\*\*\*|-{3,}|\*{3,})$/)) {
        elements.push(<hr key={`hr-${index}`} className="md-hr" />);
        return;
      }

      // 标题处理
      if (line.startsWith('#')) {
        const match = line.match(/^(#{1,6})\s+(.*)$/);
        if (match) {
          const level = match[1].length;
          const text = match[2];
          elements.push(
            React.createElement(
              `h${level}`,
              { key: `heading-${index}`, className: `md-h${level}` },
              parseInline(text),
            ),
          );
          return;
        }
      }

      // 普通段落
      elements.push(
        <p key={`paragraph-${index}`} className="md-paragraph">
          {parseInline(line)}
        </p>,
      );
    });

    // 处理未结束的列表、代码块或表格
    if (inList) {
      elements.push(<ul key="list-group-end">{listItems}</ul>);
    }
    if (inCodeBlock) {
      elements.push(
        <pre key="code-block-end" className="md-code-block">
          <code>{codeContent.join('\n')}</code>
        </pre>,
      );
    }
    if (inTable) {
      elements.push(renderTable(tableRows, 'table-end'));
    }

    return elements;
  };

  // 解析行内元素（加粗、斜体、代码、链接）
  const parseInline = (text) => {
    if (!text) return text;

    const result = [];
    let remaining = text;
    let keyIndex = 0;

    // 处理行内代码 `code`
    remaining = remaining.replace(/`([^`]+)`/g, (match, code) => {
      const key = `inline-code-${keyIndex++}`;
      result.push(
        <code key={key} className="md-inline-code">
          {code}
        </code>,
      );
      return `__MARKER_${key}__`;
    });

    // 处理加粗 **text**
    remaining = remaining.replace(/\*\*([^*]+)\*\*/g, (match, boldText) => {
      const key = `bold-${keyIndex++}`;
      result.push(
        <strong key={key} className="md-bold">
          {boldText}
        </strong>,
      );
      return `__MARKER_${key}__`;
    });

    // 处理斜体 *text*
    remaining = remaining.replace(/\*([^*]+)\*/g, (match, italicText) => {
      const key = `italic-${keyIndex++}`;
      result.push(
        <em key={key} className="md-italic">
          {italicText}
        </em>,
      );
      return `__MARKER_${key}__`;
    });

    // 处理链接 [text](url)
    remaining = remaining.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      (match, linkText, linkUrl) => {
        const key = `link-${keyIndex++}`;
        result.push(
          <a key={key} href={linkUrl} target="_blank" rel="noopener noreferrer">
            {linkText}
          </a>,
        );
        return `__MARKER_${key}__`;
      },
    );

    // 重组最终内容
    const parts = remaining.split(/__MARKER_[^_]+__/);
    const finalResult = [];

    let markerIndex = 0;
    parts.forEach((part, i) => {
      if (part) {
        finalResult.push(part);
      }
      // 插入之前解析的元素
      const nextMarker = result[markerIndex];
      if (nextMarker && i < parts.length - 1) {
        finalResult.push(nextMarker);
        markerIndex++;
      }
    });

    return finalResult.length > 0 ? finalResult : text;
  };

  // 渲染表格
  const renderTable = (rows, keyIndex) => {
    if (rows.length === 0) return null;

    // 解析表格行
    const tableData = rows.map((row) => {
      // 分割单元格，去掉首尾的 |
      const cells = row
        .trim()
        .split('|')
        .slice(1, -1)
        .map((cell) => cell.trim());
      return cells;
    });

    // 第一行是表头，第二行是分隔线（---|---），后续是数据行
    const headerRow = tableData[0];
    const separatorRow = tableData[1];
    const dataRows = tableData.slice(2);

    // 检查第二行是否是分隔线
    const isSeparator = separatorRow?.every((cell) => cell.match(/^[-:]+$/));

    return (
      <table key={`table-${keyIndex}`} className="md-table">
        <thead>
          <tr>
            {headerRow.map((cell, idx) => (
              <th key={`th-${idx}`}>{parseInline(cell)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(isSeparator ? dataRows : tableData.slice(1)).map((row, rowIdx) => (
            <tr key={`tr-${rowIdx}`}>
              {row.map((cell, cellIdx) => (
                <td key={`td-${cellIdx}`}>{parseInline(cell)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return <div className="markdown-render">{parseMarkdown(content)}</div>;
};

export default MarkdownRender;
